import React, { useEffect, useMemo, useState } from "react";
import "./../../../styles/SupportDashboard.css";
import Button from './../../ui/Button';
import UserService from './../../../services/UserService';
import SearchableUserSelect from "../../SearchableUserSelect";
import userService from "./../../../services/UserService";
import { useAuth } from "../../../context/AuthContext";
import { UserType } from './../../../constants/userType';

export default function EditUserModal({ isOpen, onClose, sites = [], onSubmit }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [email, setEmail] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [type, setType] = useState("support");
  const [groupQuery, setGroupQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState({});
  const [userPlatform, setUserPlatform] = useState("mobile");
  const [msiFile, setMsiFile] = useState("");
  const [macPack, setMacPack] = useState("");
  const [chromeUrl, setChromeUrl] = useState("");
  const [newGroupId, setNewGroupId] = useState("");
  const [newGroupQuery, setNewGroupQuery] = useState("");
  const [enabled, setEnabled] = useState("")
  const { userType } = useAuth();
  const isSuperUser = userType === UserType.SUPER;

useEffect(() => {
    if (!isOpen) return;
    setUsername("");
    setPassword("");
    setSearchError("");
    setSelectedLocations([]);
    setType("support");
    setGroupQuery("");
    setSaving(false);
    setErr({});
    setEmail("");
    setUserPlatform("mobile");
    setMsiFile("");
    setMacPack("");
    setChromeUrl("");
    setNewGroupId("");
    setNewGroupQuery("");
  }, [isOpen]);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const MIN_QUERY = 3;

  const fetchUsers = React.useCallback(async (query = "", showAll = false) => {
    try {
      setUsersLoading(true);
      setUsersError(null);

     if (!showAll && (!query || query.length < MIN_QUERY)) {
  
        return;
      }

      const response = await (userPlatform === 'mobile'
        ? userService.getAnalystsList(query)
        : userService.getInstallAccountNames(query));

        const raw = Array.isArray(response) ? response : [];
      const normalized = raw.map((u, idx) => ({
        id:
          u.id ??
          u.userId ??
          u.visorId ??
          idx,
        username:
          u.username ??
          u.userName ??
          u.name ??
          u.displayName ??
          u.email ??
          u.visorId ??
          "Unknown",
      }));
      setUsers(normalized);
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsersError("Failed to load users");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  },[userPlatform]);

  
  const handleUserQueryChange = React.useCallback((q) => {
     if (q && q.length >= 3) {
       fetchUsers(q, false);
     }
  }, [fetchUsers]);

  const siteOptions = useMemo(() => {
    const normalized = (sites || [])
      .map((s) => ({
        label: s.sitename,
        value: String(s.id),
        level: s.level || "",
        parentId: s.parentId
      }))
      .filter((s) => s.value !== "1"); 

    const q = groupQuery.trim().toLowerCase();
    const filtered = !q
      ? normalized
      : normalized.filter((x) =>
          `${x.label} ${x.level} ${x.value}`.toLowerCase().includes(q)
        );

    const limit = q ? 50 : 200;
    return filtered.slice(0, limit);
  }, [sites, groupQuery]);

  const selectedText = useMemo(() => {
    return selectedLocations.join(",");
  }, [selectedLocations]);

  const emailMissing = userPlatform === "mobile" && !email.trim();

  const toggleLocation = (id) => {
    setSelectedLocations((prev) => {
      const exists = prev.includes(id);
      if (exists) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const clearLocations = () => {
    setSelectedLocations([]);
  };

  const validate = () => {
    const e = {};

    if (userPlatform === "mobile" && selectedLocations.length === 0) {
        e.location = "Select at least one site.";
    }

    if (userPlatform === "pc" && !newGroupId) {
      e.newGroupId = "Select an Android group.";
    }

    if (userPlatform === "mobile" && !email.trim()) {
      e.email = "Email is required.";
    }
        
    if (userPlatform === "pc") {
      if (!msiFile.trim()) e.msiFile = "MSI File URL is required.";
      if (!chromeUrl.trim()) e.chromeUrl = "Chrome URL is required.";
    }

    setErr(e);
    return Object.keys(e).length === 0;
  };
function extractApiError(ex) {

  const data = ex?.response?.data;
  const payload = data ?? ex?.data ?? ex?.body ?? null;
  const errors = payload?.errors;
  const message =
    payload?.message ||
    (Array.isArray(errors) && errors[0]?.message) ||
    ex?.message ||
    "Request failed.";

  return { payload, message, errors: Array.isArray(errors) ? errors : [] };
}

  const canSubmit = useMemo(() => {
    if (saving || !username.trim() || !userPlatform) {
      return false;
    }

    if (userPlatform === "mobile") {
      return selectedLocations.length > 0 && !!type && !!email.trim();
    }

    if (userPlatform === "pc") {
      return !!newGroupId && !!msiFile.trim() && !!chromeUrl.trim();
    }

    return false;
    }, [username, selectedLocations, type, userPlatform, email, newGroupId, msiFile, chromeUrl, saving]);

  const buildUserPayload = () => {
    const basePayload = {
      username: username.trim(),
      email: email.trim(),
    };

    if (userPlatform === "mobile") {
      return {
        ...basePayload,
        type: type.trim().toLowerCase(),
        location: selectedLocations.join(","),
        enabled : enabled === true,
      };
    } else if (userPlatform === "pc") {
      return {
        ...basePayload,
        groupId: newGroupId,
        msiFile,
        macPack,
        chromeUrl,
        password: password
      };
    }

    return basePayload;
  };

  const submit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = buildUserPayload();
      await onSubmit(payload, userPlatform);
      onClose();
} catch (ex) {
  const { message, errors } = extractApiError(ex);

  const fieldErrors = {};
  const messages = [];

  for (const e of errors) {
    if (!e?.message) continue;
    messages.push(e.message);

    if (e.target) {
      fieldErrors[e.target] = e.message;
      continue;
    }

    const msg = e.message.toLowerCase();
    if (msg.includes("username")) fieldErrors.username = e.message;
    if (msg.includes("email")) fieldErrors.email = e.message;
    if (msg.includes("location") || msg.includes("group")) fieldErrors.location = e.message;
    if (msg.includes("type")) fieldErrors.type = e.message;
  }

  setErr((prev) => ({
    ...prev,
    ...fieldErrors,
    api: messages.length ? messages.join(" • ") : message,
  }));
} finally {
      setSaving(false);
    }
  };
    

const handleSelectUser = React.useCallback(async (selectedUsername) => {
  const u = (selectedUsername || "").trim();
  if (!u) return;

  setSearchError("");
  setSaving(true);
  try {

    const user = userPlatform === "mobile"
      ? await UserService.getMobileUser(u)
      : await UserService.getPoliceUser(u);

    if (!user) {
      setSearchError("User not found.");
      return;
    }

    setUsername(user.username ?? u);
    setEmail(user.email ?? "");

    if (userPlatform === "mobile") {
      updateMobileFields(user);
    } else {
      updatePoliceFields(user);
    }

  } catch (error) {
    setSearchError(`Error fetching data: ${error.message}`);
  } finally {
    setSaving(false);
  }
}, [userPlatform]);

const updateMobileFields = (user) => {
  const locations = user.location?.split(",").map((s) => s.trim()) ?? [];
  setSelectedLocations(locations);
  setType(user.type || "support");
};

const updatePoliceFields = (user) => {
  setNewGroupQuery(user.constabulary ?? "");
  setNewGroupId(user.groupId ?? user.constabulary ?? "");
  setMsiFile(user.msiFile || "");
  setMacPack(user.macPack || "");
  setChromeUrl(user.chromeUrl || "");
  setPassword("");
};
   

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-md">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Edit User</h2>
                <div className="sd-subtitle">Edit an existing user's account.</div>
          </div>
          <button className="sd-icon-btn" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        <div className="sd-modal-body">
          {err.api && <div className="sd-alert sd-alert-bad">{err.api}</div>}

          <div className="sd-field">
            <div className="sd-label">User Platform</div>
            <div className="sd-seg" role="tablist">
              <button 
                type="button"
                role="tab"
                aria-selected={userPlatform === "mobile"}
                aria-controls="mobile-panel"
                className={`sd-seg-btn ${userPlatform === "mobile" ? "is-active" : ""}`} 
                onClick={() => { 
                  setUsername("")
                  setSelectedLocations([]);
                  setNewGroupId("");
                  setNewGroupQuery("");
                  setGroupQuery("");
                  setUsername("");
                  setEmail("");
                  setMsiFile("");
                  setMacPack("");
                  setChromeUrl("");
                  setPassword("");
                  setUserPlatform("mobile");
                }}
              >
                Mobile
              </button>
              <button 
                type="button"
                role="tab"
                aria-selected={userPlatform === "pc"}
                aria-controls="pc-panel"
                className={`sd-seg-btn ${userPlatform === "pc" ? "is-active" : ""}`} 
                onClick={() => { 
                  setUsername("")
                  setSelectedLocations([]);
                  setNewGroupId("");
                  setNewGroupQuery("");
                  setGroupQuery("");
                  setUsername("");
                  setEmail("");
                  setMsiFile("");
                  setMacPack("");
                  setChromeUrl("");
                  setPassword("");
                  setUserPlatform("pc");
                }}
              >
                Install Portal Account
              </button>
            </div>
            {err.platform && <div className="sd-error">{err.platform}</div>}
          </div>

          <div className="sd-label">User</div>
          <div>        
              <SearchableUserSelect
                value={username}
                onSelect={handleSelectUser}
                options={users}
                loading={usersLoading}
                onQueryChange={handleUserQueryChange}
                placeholder="Search users…"
              />
          </div>    


          {userPlatform === "mobile" && (
          <>

          <div className="sd-field">
            <div className="sd-label">Email *</div>
            <input
              className={`sd-input ${err.email || emailMissing ? "has-error" : ""}`}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (err.email) {
                  setErr((current) => ({ ...current, email: undefined }));
                }
              }}
              placeholder="e.g. user@example.com"
              autoComplete="off"
              type="email"
              required
              aria-required="true"
              aria-invalid={emailMissing || Boolean(err.email)}
              aria-describedby={emailMissing || err.email ? "edit-user-email-error" : undefined}
            />
            {(err.email || emailMissing) && (
              <div id="edit-user-email-error" className="sd-error" role="alert">
                {err.email || "Email is required."}
              </div>
            )}
          </div>
          <div className="sd-divider" />

          <div className="sd-field">
            <div className="sd-label">User type</div>
            <div className="sd-seg">
              <button type="button" className={`sd-seg-btn ${type === "police" ? "is-active" : ""}`} onClick={() => setType("police")}>
                Police
              </button>
              <button type="button" className={`sd-seg-btn ${type === "teamleader" ? "is-active" : ""}`} onClick={() => setType("teamleader")}>
                Team Leader
              </button>
              <button type="button" className={`sd-seg-btn ${type === "analyst" ? "is-active" : ""}`} onClick={() => setType("analyst")}>
                Analyst
              </button>
              <button type="button" className={`sd-seg-btn ${type === "support" ? "is-active" : ""}`} onClick={() => setType("support")}>
                Support
              </button>
              {isSuperUser && (
                <button type="button" className={`sd-seg-btn ${type === "super" ? "is-active" : ""}`} onClick={() => setType("super")}>
                  Super
                </button>
              )}
            </div>
            {err.type && <div className="sd-error">{err.type}</div>}
          </div>

          <div className="sd-field">
            <div className="sd-label">Assign sites</div>
            <div className="sd-field"></div>
              <input
                className="sd-input"
                value={groupQuery}
                onChange={(e) => setGroupQuery(e.target.value)}
                placeholder="Search sites…"
                autoComplete="off"
              />
              <Button type="button" buttonStyle="btn-alternate" onClick={clearLocations} disabled={selectedLocations.length === 0}>
                Clear Selected Locations
              </Button>
          </div>

          <div className="sd-help">
              Selected IDs: <strong>{selectedText || "None"}</strong>
          </div>
            {err.location && <div className="sd-error">{err.location}</div>}

          <div className="sd-dropdown">
              {siteOptions.length === 0 ? (
                <div className="sd-dropdown-empty">No matches</div>
              ) : (
                siteOptions.map((s) => {
                  const isSel = selectedLocations.includes(s.value);
                  return (
                    <button
                      key={s.value}
                      type="button"
                      className={`sd-dd-item ${isSel ? "is-selected" : ""}`}
                      onClick={() => toggleLocation(s.value)}
                      aria-pressed={isSel}
                    >
                      <span className="sd-dd-label">
                        <span className={`sd-dd-check ${isSel ? "is-on" : ""}`}>{isSel ? "✓" : ""}</span>
                        {s.label}
                      </span>
                      <span className="sd-dd-meta">{s.level ? s.level : `#${s.value}`}</span>
                    </button>
                  );
                })
              )}
            </div>   

          </>
          )} 

       {userPlatform === "pc" && (
          <>

            <div className="sd-field">
            <div className="sd-label">Password</div>
            <div className="sd-inline">
              <input
                className={`sd-input ${err.password ? "has-error" : ""}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                placeholder="Enter a strong password…"
                autoComplete="new-password"
              />
              <Button type="button" buttonStyle="btn-alternate" onClick={() => setShowPw((v) => !v)}>
                {showPw ? "Hide" : "Show"}
              </Button>
            </div>
            {err.password && <div className="sd-error">{err.password}</div>}
            <div className="sd-help">(NCSC) recommends that the best passwords are made up of three random words</div>           
          </div>

          <div className="sd-field">
            <div className="sd-label">Assign Android Group</div>
            <div className="sd-field">
              <input
              className="sd-input"
              value={newGroupQuery}
              onChange={(e) => {
              setNewGroupQuery(e.target.value);
              if (newGroupId) setNewGroupId(null); 
            }}
            placeholder="Search destination group…"
            autoComplete="off"
              />

              <div className="sd-dropdown">
                {siteOptions.length === 0 ? (
                  <div className="sd-dropdown-empty">No matches</div>
                ) : (
                  siteOptions
                  .filter(s => s.label.toLowerCase().includes(newGroupQuery.toLowerCase()))
                  .map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      className={`sd-dd-item ${String(newGroupId) === String(s.value) ? "is-selected" : ""}`}
                      onClick={() => {
                        setNewGroupId(s.value);
                        setNewGroupQuery(s.label);
                      }}
                    >
                      <span className="sd-dd-label">{s.label}</span>
                      <span className="sd-dd-meta">{s.level ? s.level : `#${s.value}`}</span>
                    </button>
                  ))
                )}
              </div>
              {err.newGroupId && <div className="sd-error">{err.newGroupId}</div>}
            </div>
          </div>

          <div className="sd-field">
            <div className="sd-label">MSI File</div>
            <input
              className={`sd-input ${err.msiFile ? "has-error" : ""}`}
              value={msiFile}
              onChange={(e) => setMsiFile(e.target.value)}
              placeholder="https://vm14.esafeglobal.com/msi/SITE-NAME/eSafeInstaller.exe"
              autoComplete="off"
            />
            {err.msiFile && <div className="sd-error">{err.msiFile}</div>}
          </div>
          
          <div className="sd-field">
            <div className="sd-label">Mac Pac</div>
            <input
              className={`sd-input ${err.macPack ? "has-error" : ""}`}
              value={macPack}
              onChange={(e) => setMacPack(e.target.value)}
              placeholder="https://vm14.esafeglobal.com/MACs/SITE-NAME/macos.mpkg.zip"
              autoComplete="off"
            />
            {err.macPack && <div className="sd-error">{err.macPack}</div>}
          </div>
 
          <div className="sd-field">
            <div className="sd-label">Chrome URL</div>
            <input
              className={`sd-input ${err.chromeUrl ? "has-error" : ""}`}
              value={chromeUrl}
              onChange={(e) => setChromeUrl(e.target.value)}
              placeholder="https://vm25.esafeglobal.com/SITE-NAME/SITEID"
              autoComplete="off"
            />
            {err.chromeUrl && <div className="sd-error">{err.chromeUrl}</div>}
          </div>
          </>
          )}
        </div>

        <div className="sd-modal-footer">
          <Button onClick={onClose} buttonStyle="btn-alternate" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} buttonStyle="btn-main" disabled={!canSubmit}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
